/**
 * /config — superuser admin panel.
 *
 * Reachable only by direct URL; not linked from the main nav. Flow:
 *   1. user lands on /config → prompted for the shared "config key"
 *   2. key + SSO email is verified by hitting `/config-admin/unlock`
 *   3. on success the four sub-panels become visible
 *
 * The key is held in module-scope memory only (state/configKey.ts); tab
 * close clears it.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  adminCreateUser,
  adminDeleteGeography,
  adminDeleteUser,
  adminGetSettings,
  adminListGeographies,
  adminListUsers,
  adminSaveGeography,
  adminTriggerSync,
  adminUnlock,
  adminUpdateSettings,
  adminUpdateUser,
  ApiError,
} from '../api';
import type { AdminGeography, AdminSettingsPayload, AdminUser } from '../api';
import { getActiveConfigKey, setActiveConfigKey } from '../state/configKey';

type UnlockState =
  | { status: 'locked'; error: string | null }
  | { status: 'verifying' }
  | { status: 'unlocked' };

export function ConfigPage() {
  const [unlock, setUnlock] = useState<UnlockState>(
    getActiveConfigKey() ? { status: 'verifying' } : { status: 'locked', error: null },
  );
  const [keyInput, setKeyInput] = useState('');

  const tryUnlock = useCallback(async () => {
    setUnlock({ status: 'verifying' });
    try {
      await adminUnlock();
      setUnlock({ status: 'unlocked' });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? typeof e.detail === 'object' && e.detail && 'detail' in e.detail
            ? String((e.detail as { detail: string }).detail)
            : String(e.message)
          : e instanceof Error
            ? e.message
            : String(e);
      setActiveConfigKey(null);
      setUnlock({ status: 'locked', error: msg });
    }
  }, []);

  useEffect(() => {
    if (unlock.status === 'verifying') {
      tryUnlock();
    }
  }, [unlock.status, tryUnlock]);

  if (unlock.status !== 'unlocked') {
    return (
      <div className="max-w-md mx-auto mt-16 bg-white rounded-xl shadow-card p-6 space-y-4">
        <h1 className="text-lg font-semibold text-slate-900">Admin config</h1>
        <p className="text-sm text-slate-600">
          Enter the shared admin key to continue. This is in addition to your
          Microsoft sign-in.
        </p>
        <input
          type="password"
          autoFocus
          autoComplete="off"
          className="input"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder="X-Config-Key"
        />
        {unlock.status === 'locked' && unlock.error ? (
          <div
            role="alert"
            className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2"
          >
            {unlock.error}
          </div>
        ) : null}
        <button
          className="btn-primary w-full"
          type="button"
          disabled={!keyInput || unlock.status === 'verifying'}
          onClick={() => {
            setActiveConfigKey(keyInput);
            setUnlock({ status: 'verifying' });
          }}
        >
          {unlock.status === 'verifying' ? 'Verifying…' : 'Unlock'}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Admin config</h1>
        <p className="text-sm text-slate-500">
          Superuser-only. Changes take effect immediately.
        </p>
      </header>
      <UsersPanel />
      <GeographiesPanel />
      <SyncPanel />
      <SettingsPanel />
    </div>
  );
}

// ─── Users ────────────────────────────────────────────────────────────


function UsersPanel() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<'lpo' | 'admin' | 'superuser'>('lpo');

  const refresh = useCallback(async () => {
    try {
      setUsers(await adminListUsers());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function addUser() {
    if (!newEmail.includes('@') || !newName.trim()) return;
    try {
      await adminCreateUser({ email: newEmail.trim(), full_name: newName.trim(), role: newRole });
      setNewEmail('');
      setNewName('');
      setNewRole('lpo');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Section title="Users" subtitle="People allowed to use the app. SSO email must match Entra ID.">
      {error ? <ErrorBanner message={error} /> : null}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <input
          className="input"
          placeholder="email@danone.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <input
          className="input"
          placeholder="Full name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <select
          className="input"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as typeof newRole)}
        >
          <option value="lpo">LPO</option>
          <option value="admin">Admin</option>
          <option value="superuser">Superuser</option>
        </select>
        <button className="btn-primary" type="button" onClick={addUser}>
          Add user
        </button>
      </div>
      <DataTable
        headers={['Email', 'Name', 'Role', 'Active', 'Last login', '']}
        rows={(users ?? []).map((u) => [
          <span key={`${u.id}-email`} className="font-mono text-xs">{u.email}</span>,
          u.full_name,
          <RoleSelect
            key={`${u.id}-role`}
            user={u}
            onChange={async (role, is_active) => {
              await adminUpdateUser(u.id, { role, is_active });
              await refresh();
            }}
          />,
          <ActiveToggle
            key={`${u.id}-active`}
            user={u}
            onChange={async (is_active) => {
              await adminUpdateUser(u.id, { role: u.role, is_active });
              await refresh();
            }}
          />,
          u.last_login_at ?? '—',
          <button
            key={`${u.id}-del`}
            type="button"
            className="text-rose-600 text-sm hover:underline"
            onClick={async () => {
              if (!confirm(`Delete ${u.email}?`)) return;
              try {
                await adminDeleteUser(u.id);
                await refresh();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Delete
          </button>,
        ])}
      />
    </Section>
  );
}

function RoleSelect({
  user,
  onChange,
}: {
  user: AdminUser;
  onChange: (role: AdminUser['role'], is_active: boolean) => Promise<void> | void;
}) {
  return (
    <select
      className="input"
      value={user.role}
      onChange={(e) => onChange(e.target.value as AdminUser['role'], user.is_active)}
    >
      <option value="lpo">LPO</option>
      <option value="admin">Admin</option>
      <option value="superuser">Superuser</option>
    </select>
  );
}

function ActiveToggle({
  user,
  onChange,
}: {
  user: AdminUser;
  onChange: (is_active: boolean) => Promise<void> | void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={user.is_active}
        onChange={(e) => onChange(e.target.checked)}
      />
      {user.is_active ? 'Active' : 'Disabled'}
    </label>
  );
}

// ─── Geographies ──────────────────────────────────────────────────────


function GeographiesPanel() {
  const [geos, setGeos] = useState<AdminGeography[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [countries, setCountries] = useState('');

  const refresh = useCallback(async () => {
    try {
      setGeos(await adminListGeographies());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function save() {
    if (!code || !name) return;
    try {
      await adminSaveGeography({
        code: code.trim(),
        name: name.trim(),
        countries: countries.trim(),
        sort_order: geos?.length ?? 0,
      });
      setCode('');
      setName('');
      setCountries('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Section
      title="Geographies"
      subtitle="LPO review buckets. `countries` is a comma-separated ISO 3166-1 alpha-2 list."
    >
      {error ? <ErrorBanner message={error} /> : null}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <input className="input" placeholder="Code (EU)" value={code} onChange={(e) => setCode(e.target.value)} />
        <input className="input" placeholder="Name (Europe)" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="input" placeholder="FR,DE,IT" value={countries} onChange={(e) => setCountries(e.target.value)} />
        <button className="btn-primary" type="button" onClick={save}>
          Add / update
        </button>
      </div>
      <DataTable
        headers={['Code', 'Name', 'Countries', 'Sort', '']}
        rows={(geos ?? []).map((g) => [
          <span key={`${g.code}-code`} className="font-mono text-xs">{g.code}</span>,
          g.name,
          <span key={`${g.code}-c`} className="text-xs text-slate-600 break-all">
            {g.countries || '—'}
          </span>,
          g.sort_order,
          <button
            key={`${g.code}-del`}
            type="button"
            className="text-rose-600 text-sm hover:underline"
            onClick={async () => {
              if (!confirm(`Delete geography ${g.code}?`)) return;
              try {
                await adminDeleteGeography(g.code);
                await refresh();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              }
            }}
          >
            Delete
          </button>,
        ])}
      />
    </Section>
  );
}

// ─── Sync ────────────────────────────────────────────────────────────


function SyncPanel() {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function go(forceFull: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await adminTriggerSync(forceFull);
      setStatus(
        res.already_running
          ? `Sync ${res.job_id} already running (since ${res.started_at}).`
          : `Started sync ${res.job_id}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Sync"
      subtitle="Pull the full ServiceNow snapshot. Force-full ignores per-phase cache."
    >
      <div className="flex flex-wrap gap-2">
        <button className="btn-primary" type="button" disabled={busy} onClick={() => go(false)}>
          {busy ? 'Working…' : 'Start sync (cached)'}
        </button>
        <button className="btn-secondary" type="button" disabled={busy} onClick={() => go(true)}>
          Force-full sync
        </button>
      </div>
      {status ? <p className="mt-3 text-sm text-slate-600">{status}</p> : null}
      {error ? <ErrorBanner message={error} className="mt-3" /> : null}
    </Section>
  );
}

// ─── Settings ────────────────────────────────────────────────────────


function SettingsPanel() {
  const [data, setData] = useState<AdminSettingsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const refresh = useCallback(async () => {
    try {
      const next = await adminGetSettings();
      setData(next);
      setDraft({
        servicenow_instance_url: next.values.servicenow_instance_url?.value ?? '',
        servicenow_test_url: next.values.servicenow_test_url?.value ?? '',
        servicenow_username: next.values.servicenow_username?.value ?? '',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function save() {
    if (!data) return;
    try {
      await adminUpdateSettings(
        draft as Partial<Record<'servicenow_instance_url' | 'servicenow_test_url' | 'servicenow_username', string>>,
      );
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (!data) {
    return <Section title="ServiceNow runtime settings">Loading…</Section>;
  }

  return (
    <Section
      title="ServiceNow runtime settings"
      subtitle="Overrides win over env. Password lives in Key Vault and is read-only."
    >
      {error ? <ErrorBanner message={error} /> : null}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-2 pr-4 font-medium">Setting</th>
            <th className="py-2 pr-4 font-medium">Value</th>
            <th className="py-2 pr-4 font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(data.values).map(([k, v]) => (
            <tr key={k} className="border-t border-slate-200">
              <td className="py-2 pr-4 font-mono text-xs">{k}</td>
              <td className="py-2 pr-4">
                <input
                  className="input"
                  value={draft[k] ?? ''}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                />
                <div className="text-xs text-slate-400 mt-1">env fallback: {v.env_var}</div>
              </td>
              <td className="py-2 pr-4 text-xs text-slate-500">
                {v.source} {v.updated_by ? `· by ${v.updated_by}` : ''}
              </td>
            </tr>
          ))}
          <tr className="border-t border-slate-200">
            <td className="py-2 pr-4 font-mono text-xs">servicenow_password</td>
            <td className="py-2 pr-4 text-sm text-slate-500">{data.password_status}</td>
            <td className="py-2 pr-4 text-xs text-slate-500">key vault</td>
          </tr>
        </tbody>
      </table>
      <div className="mt-4">
        <button className="btn-primary" type="button" onClick={save}>
          Save overrides
        </button>
      </div>
    </Section>
  );
}

// ─── Layout helpers ──────────────────────────────────────────────────


function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-xl shadow-card p-6">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      {subtitle ? <p className="text-xs text-slate-500 mt-1 mb-4">{subtitle}</p> : <div className="mb-4" />}
      {children}
    </section>
  );
}

function DataTable({
  headers,
  rows,
}: {
  headers: (string | React.ReactNode)[];
  rows: React.ReactNode[][];
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">No rows.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500">
            {headers.map((h, i) => (
              <th key={i} className="py-2 pr-4 font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, i) => (
            <tr key={i} className="border-t border-slate-200 align-top">
              {cells.map((cell, j) => (
                <td key={j} className="py-2 pr-4">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErrorBanner({ message, className }: { message: string; className?: string }) {
  return (
    <div
      role="alert"
      className={`text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-3 ${className ?? ''}`}
    >
      {message}
    </div>
  );
}
